/**
 * Presents the immutable ServiceNow table and bounded first-transfer scope.
 *
 * ServiceNow checkpoints use table sys_ids as durable document identity, so
 * this component keeps the table choice, initial snapshot, and record ceiling
 * together while the feed form remains responsible for persistence.
 */
import { Check, LockKeyhole, TriangleAlert } from 'lucide-react'

import {
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import type { ConnectorInitialSync } from '../../types'
import type {
  ServiceNowFilter,
  ServiceNowTable,
  ServiceNowTableAccess,
  ServiceNowTableAccessCatalog,
  ServiceNowTableDefinitions,
  ServiceNowSelectionPreview,
} from '../types'
import { serviceNowTableLabels } from '../types'
import {
  ServiceNowFilters,
  setServiceNowFilterValue,
} from './ServiceNowFilters'
import { ServiceNowSelectionPreview as SelectionPreview } from './ServiceNowSelectionPreview'

export function ServiceNowTableScope({
  table,
  onTableChange,
  recordLimit,
  onRecordLimitChange,
  filters,
  onFiltersChange,
  definitions,
  accessCatalog,
  loadingTables,
  loadingTableAccess,
  onLoadTableAccess,
  selectionPreview,
  loadingSelectionPreview,
  selectionPreviewError,
  initialSync,
  onInitialSyncChange,
  editing,
  canEditQuery,
}: {
  table: ServiceNowTable
  onTableChange: (table: ServiceNowTable) => void
  recordLimit: number
  onRecordLimitChange: (limit: number) => void
  filters: ServiceNowFilter[]
  onFiltersChange: (filters: ServiceNowFilter[]) => void
  definitions: ServiceNowTableDefinitions | null
  accessCatalog: ServiceNowTableAccessCatalog | null
  loadingTables: boolean
  loadingTableAccess: boolean
  onLoadTableAccess: () => void
  selectionPreview: ServiceNowSelectionPreview | null
  loadingSelectionPreview: boolean
  selectionPreviewError?: string
  initialSync: ConnectorInitialSync
  onInitialSyncChange: (value: ConnectorInitialSync) => void
  editing: boolean
  canEditQuery: boolean
}) {
  const definition = definitions?.supportedTables.find(item => item.table === table)
  const accessByTable = new Map(
    accessCatalog?.commonTables.map(item => [item.table, item.access]) ?? [],
  )
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-slate-700">ServiceNow table</Label>
        <Select
          value={table}
          disabled={!canEditQuery || editing || loadingTables}
          onValueChange={value => onTableChange(value as ServiceNowTable)}
        >
          <SelectTrigger
            aria-label="ServiceNow table"
            className="h-[54px] w-full rounded-[9px] border-slate-200 px-3"
          >
            <span className="flex min-w-0 items-center gap-2.5 text-left">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-700">
                <TriangleAlert className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-semibold text-slate-900">
                  {serviceNowTableLabels[table]}
                </span>
                <span className="block truncate font-mono text-xs text-slate-500">
                  {table} · tracked by sys_id revision
                </span>
              </span>
            </span>
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            {(Object.entries(serviceNowTableLabels) as [ServiceNowTable, string][])
              .map(([value, label]) => (
                <SelectItem
                  key={value}
                  value={value}
                  disabled={accessByTable.get(value) === 'denied'}
                >
                  {label}{accessByTable.get(value) === 'denied' ? ' — not authorized' : ''}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <p className="text-xs leading-4 text-slate-400">
          Scope and schedule below belong to this table. More approved tables can be added to this connection later.
        </p>
      </div>

      {definition && (
        <>
          <ServiceNowFilters
            definition={definition}
            filters={filters}
            preview={selectionPreview}
            canEdit={canEditQuery}
            onChange={onFiltersChange}
          />
          <SelectionPreview
            preview={selectionPreview}
            loading={loadingSelectionPreview}
            error={selectionPreviewError}
            onFilter={(field, value) => {
              if (!canEditQuery) return
              onFiltersChange(
                setServiceNowFilterValue(definition, filters, field, value),
              )
            }}
          />
        </>
      )}

      {loadingTables && (
        <p role="status" className="text-xs text-slate-500">Loading table filters…</p>
      )}

      {canEditQuery && <ServiceNowCatalogSummary
        catalog={accessCatalog}
        loading={loadingTableAccess}
        onLoad={onLoadTableAccess}
      />}

      {!editing && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-slate-700">First run</Label>
          <RadioGroup
            value={initialSync}
            onValueChange={value => onInitialSyncChange(value as ConnectorInitialSync)}
            className="grid gap-2.5 sm:grid-cols-2"
          >
            <ScopeChoice
              value="all"
              selected={initialSync === 'all'}
              title="Import current records"
            >
              Then sync only new or changed records
            </ScopeChoice>
            <ScopeChoice
              value="future"
              selected={initialSync === 'future'}
              title="Start with future changes"
            >
              Record today&apos;s state without importing it
            </ScopeChoice>
          </RadioGroup>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        <Label htmlFor="servicenow-record-limit" className="text-xs font-medium text-slate-700">
          Record limit
        </Label>
        <Input
          id="servicenow-record-limit"
          type="number"
          min={1}
          max={500}
          value={recordLimit}
          disabled={!canEditQuery}
          onChange={event => onRecordLimitChange(Number(event.target.value))}
          className="h-8 w-[106px] text-[13px]"
        />
        <span className="text-xs text-slate-400">
          Window of newest records the filters run against (1–500).
          {selectionPreview && (
            <> {selectionPreview.matchCount} of {selectionPreview.windowCount} currently pass.</>
          )}
        </span>
      </div>
    </div>
  )
}

function ServiceNowCatalogSummary({
  catalog,
  loading,
  onLoad,
}: {
  catalog: ServiceNowTableAccessCatalog | null
  loading: boolean
  onLoad: () => void
}) {
  const verified = catalog?.commonTables.filter(
    table => table.access === 'readable' || table.access === 'empty',
  ) ?? []
  return (
    <details
      className="rounded-[9px] border border-slate-200 bg-white text-xs text-slate-600"
      onToggle={event => {
        if (event.currentTarget.open) onLoad()
      }}
    >
      <summary className="cursor-pointer px-3 py-2.5 font-medium text-slate-700">
        Common table access
        {catalog && ` · ${verified.length} of ${catalog.commonTables.length} reachable`}
      </summary>
      {loading && (
        <p role="status" className="border-t border-slate-100 px-3 py-3 leading-4 text-slate-500">
          Checking common table access…
        </p>
      )}
      {catalog && (
        <>
          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-3 pt-3">
            {catalog.commonTables.map(candidate => (
              <span
                key={candidate.table}
                title={`${candidate.module}: ${candidate.label}`}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-[11px]',
                  isReachable(candidate.access)
                    ? 'border-emerald-100 bg-emerald-50/60 text-slate-700'
                    : 'border-slate-200 bg-slate-50 text-slate-400',
                )}
              >
                {isReachable(candidate.access)
                  ? <Check className="h-3 w-3 text-emerald-600" />
                  : <LockKeyhole className="h-3 w-3" />}
                {candidate.table}
              </span>
            ))}
          </div>
          <p className="px-3 pb-3 pt-2 leading-4 text-slate-500">
            Reachability reflects this credential. Approval to import a table is managed separately.
          </p>
        </>
      )}
    </details>
  )
}

function isReachable(access: ServiceNowTableAccess) {
  return access === 'readable' || access === 'empty'
}

function ScopeChoice({
  value,
  selected,
  title,
  children,
}: {
  value: ConnectorInitialSync
  selected: boolean
  title: string
  children: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-[9px] border px-3.5 py-2.5 transition-colors',
        selected
          ? 'border-[1.5px] border-slate-900 bg-white'
          : 'border-slate-200 hover:bg-slate-50',
      )}
    >
      <RadioGroupItem value={value} />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900">{title}</span>
        <span className="block truncate text-xs text-slate-500">{children}</span>
      </span>
    </label>
  )
}
